import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { translate } from '../../../utils/Common';
import styles from './SearchFilter.module.css';
import SearchInputWithField from '../../common/SearchInputWithField';
import Input from '../../common/Input';
import Select from '../../common/Select';
import IconSelect from '../../common/IconSelect';
import Button from '../../common/Button';
import * as FaIcons from 'react-icons/fa';

const SearchFilter = ({
  onSearch,
  onFilter,
  onClear,
  filters = [],
  activeFilters = {},
  placeholder = 'Search...',
  showClearButton = true,
  className = '',
  loading = false,
  unifiedMode = false,
  selectedField = 'all',
  onFieldChange,
  fieldOptions = [],
  statusFilter = false,
  selectedStatus = 'all',
  onStatusChange,
  statusOptions = [],
  clientTypeFilter = false,
  selectedClientType = 'all',
  onClientTypeChange,
  clientTypeOptions = [],
  roleFilter = false,
  selectedRole = 'all',
  onRoleChange,
  roleOptions = [],
  formatChipValue = null,
  formatChipLabel = null,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [currentSelectedField, setCurrentSelectedField] = useState(selectedField || 'name');
  const [currentSelectedStatus, setCurrentSelectedStatus] = useState(selectedStatus ?? 'all');
  const [currentSelectedClientType, setCurrentSelectedClientType] = useState(selectedClientType ?? 'all');
  const [currentSelectedRole, setCurrentSelectedRole] = useState(selectedRole ?? 'all');

  useEffect(() => {
    setFilterValues(activeFilters);
  }, [activeFilters]);

  const handleFilterChange = (filterName, value) => {
    const newFilters = { ...filterValues, [filterName]: value };
    setFilterValues(newFilters);
  };

  const handleSearch = () => {
    if (unifiedMode) {
      // En modo unificado, agregar o reemplazar el filtro del campo seleccionado
      if (searchTerm && searchTerm.trim()) {
        // Agregar o reemplazar el filtro del campo seleccionado
        const newFilters = { 
          ...activeFilters, 
          [currentSelectedField]: searchTerm.trim() 
        };
        
        // Agregar filtro de status si está habilitado
        if (statusFilter && currentSelectedStatus && currentSelectedStatus !== 'all') {
          newFilters.status = currentSelectedStatus;
        }
        // Agregar filtro de clientType si está habilitado
        if (clientTypeFilter && currentSelectedClientType && currentSelectedClientType !== 'all') {
          newFilters.clientType = currentSelectedClientType;
        }
        // Agregar filtro de role si está habilitado
        if (roleFilter && currentSelectedRole && currentSelectedRole !== 'all') {
          newFilters.role = currentSelectedRole;
        }
        
        if (onFilter) {
          onFilter(newFilters);
        }
        
        // Limpiar el input después de agregar
        setSearchTerm('');
      } else {
        // Si no hay término de búsqueda, solo actualizar filtros especiales
        const newFilters = { ...activeFilters };
        if (statusFilter && currentSelectedStatus && currentSelectedStatus !== 'all') {
          newFilters.status = currentSelectedStatus;
        } else if (statusFilter) {
          delete newFilters.status;
        }
        if (clientTypeFilter && currentSelectedClientType && currentSelectedClientType !== 'all') {
          newFilters.clientType = currentSelectedClientType;
        } else if (clientTypeFilter) {
          delete newFilters.clientType;
        }
        if (roleFilter && currentSelectedRole && currentSelectedRole !== 'all') {
          newFilters.role = currentSelectedRole;
        } else if (roleFilter) {
          delete newFilters.role;
        }
        if (onFilter) {
          onFilter(newFilters);
        }
      }
      
      if (onSearch) {
        onSearch(searchTerm, currentSelectedField);
      }
    } else {
      // Modo tradicional
      if (onSearch) {
        onSearch(searchTerm);
      }
      if (onFilter) {
        onFilter(filterValues);
      }
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    if (unifiedMode) {
      if (onClear) {
        onClear();
      } else {
        const defaultField = fieldOptions?.[0]?.value || 'numeroTracking';
        setCurrentSelectedField(defaultField);
        setCurrentSelectedStatus('all');
        setCurrentSelectedClientType('all');
        setCurrentSelectedRole('all');
        if (onFieldChange) onFieldChange(defaultField);
        if (onStatusChange) onStatusChange('all');
        if (onClientTypeChange) onClientTypeChange('all');
        if (onRoleChange) onRoleChange('all');
        if (onSearch) onSearch('', defaultField);
        if (onFilter) onFilter({});
      }
    } else {
      const clearedFilters = {};
      filters.forEach(filter => {
        clearedFilters[filter.name] = '';
      });
      setFilterValues(clearedFilters);
      if (onSearch) onSearch('');
      if (onFilter) onFilter(clearedFilters);
    }
  };

  const handleFieldChange = (field) => {
    setCurrentSelectedField(field);
    if (onFieldChange) {
      onFieldChange(field);
    }
  };

  const handleStatusChange = (status) => {
    setCurrentSelectedStatus(status);
    if (onStatusChange) {
      onStatusChange(status);
    }
    // Aplicar filtro automáticamente cuando cambia el status
    if (onFilter && unifiedMode) {
      const newFilters = { ...activeFilters };
      if (status && status !== 'all') {
        newFilters.status = status;
      } else {
        delete newFilters.status;
      }
      onFilter(newFilters);
    }
  };

  const handleRoleInternalChange = (role) => {
    setCurrentSelectedRole(role);
    if (onRoleChange) {
      onRoleChange(role);
    }
    // Aplicar filtro automáticamente cuando cambia el role
    if (onFilter && unifiedMode) {
      const newFilters = { ...activeFilters };
      if (role && role !== 'all') {
        newFilters.role = role;
      } else {
        delete newFilters.role;
      }
      onFilter(newFilters);
    }
  };

  useEffect(() => {
    if (selectedField !== undefined) {
      setCurrentSelectedField(selectedField);
    }
  }, [selectedField]);

  useEffect(() => {
    if (selectedStatus !== undefined) {
      setCurrentSelectedStatus(selectedStatus);
    }
  }, [selectedStatus]);

  useEffect(() => {
    if (selectedClientType !== undefined) {
      setCurrentSelectedClientType(selectedClientType);
    }
  }, [selectedClientType]);

  useEffect(() => {
    if (selectedRole !== undefined) {
      setCurrentSelectedRole(selectedRole);
    }
  }, [selectedRole]);

  const handleClientTypeChange = (clientType) => {
    setCurrentSelectedClientType(clientType);
    if (onClientTypeChange) {
      onClientTypeChange(clientType);
    }
    // Aplicar filtro automáticamente cuando cambia el clientType
    if (onFilter && unifiedMode) {
      const newFilters = { ...activeFilters };
      if (clientType && clientType !== 'all') {
        newFilters.clientType = clientType;
      } else {
        delete newFilters.clientType;
      }
      onFilter(newFilters);
    }
  };

  const containerClasses = [styles.container, className].filter(Boolean).join(' ');

  // Obtener chips activos (excluir status, clientType y role que son filtros especiales)
  const activeChips = unifiedMode 
    ? Object.entries(activeFilters || {})
        .filter(([field, filterValue]) => {
          return field !== 'status' && field !== 'clientType' && field !== 'role' && filterValue && filterValue.trim();
        })
    : [];

  // Función para obtener el label del campo
  const getFieldLabel = (fieldValue) => {
    // Si hay una función personalizada para formatear el label, usarla
    if (formatChipLabel) {
      return formatChipLabel(fieldValue);
    }
    const option = fieldOptions.find(opt => opt.value === fieldValue);
    return option ? option.label : fieldValue;
  };

  return (
    <div className={containerClasses}>
      <div className={styles.searchRow}>
        {unifiedMode ? (
          <>
            {/* Grupo 1: Search Input con Field Selector integrado */}
            <div className={styles.searchGroup}>
              {fieldOptions.length > 0 ? (
                <SearchInputWithField
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={placeholder}
                  fieldOptions={fieldOptions}
                  selectedField={currentSelectedField}
                  onFieldChange={handleFieldChange}
                  disabled={loading}
                  loading={loading}
                  className={styles.searchInputWithField}
                  // No mostrar chips aquí, se mostrarán en una fila separada
                  activeFilters={{}}
                  onChipRemove={() => {}}
                  onAddFilter={(field, value) => {
                    // Agregar o reemplazar el filtro manteniendo todos los demás
                    const newFilters = { ...activeFilters, [field]: value };
                    // Mantener filtros especiales
                    if (statusFilter && currentSelectedStatus && currentSelectedStatus !== 'all') {
                      newFilters.status = currentSelectedStatus;
                    }
                    if (clientTypeFilter && currentSelectedClientType && currentSelectedClientType !== 'all') {
                      newFilters.clientType = currentSelectedClientType;
                    }
                    if (onFilter) {
                      onFilter(newFilters);
                    }
                    // Limpiar el input
                    setSearchTerm('');
                  }}
                />
              ) : (
                <div className={styles.searchInput}>
                  <Input
                    type="text"
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    leftIcon={<FaIcons.FaSearch />}
                    fullWidth
                  />
                </div>
              )}
            </div>

            {/* Grupo 2: Role + Status + Client Type */}
            <div className={styles.filterGroup}>
              {roleFilter && roleOptions.length > 0 && (
                <div className={styles.iconSelectorWrapper}>
                  <IconSelect
                    value={currentSelectedRole || 'all'}
                    onChange={handleRoleInternalChange}
                    options={roleOptions}
                    size="sm"
                    disabled={loading}
                  />
                </div>
              )}
              {statusFilter && statusOptions.length > 0 && (
                <div className={styles.fieldSelector}>
                  <IconSelect
                    value={currentSelectedStatus || 'all'}
                    onChange={handleStatusChange}
                    options={statusOptions}
                    size="md"
                    disabled={loading}
                  />
                </div>
              )}

              {clientTypeFilter && clientTypeOptions.length > 0 && (
                <div className={styles.iconSelectorWrapper}>
                  <IconSelect
                    value={currentSelectedClientType || 'all'}
                    onChange={handleClientTypeChange}
                    options={clientTypeOptions}
                    size="sm"
                    disabled={loading}
                  />
                </div>
              )}
            </div>

            {/* Grupo 3: Botones */}
            <div className={styles.buttonGroup}>
              <Button
                variant="primary"
                onClick={handleSearch}
                className={styles.searchButton}
                leftIcon={<FaIcons.FaSearch />}
                loading={loading}
                disabled={loading}
              >
                {translate('global.search')}
              </Button>

              {showClearButton && (
                <Button
                  variant="outline"
                  onClick={handleClear}
                  className={styles.clearButton}
                  leftIcon={<FaIcons.FaTimes />}
                >
                  {translate('global.clean')}
                </Button>
              )}
            </div>
          </>
        ) : (
          <>
            <div className={styles.searchInput}>
              <Input
                type="text"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                leftIcon={<FaIcons.FaSearch />}
                fullWidth
              />
            </div>

            {filters.map((filter) => (
              <div key={filter.name} className={styles.filterItem}>
                {filter.type === 'text' && (
                  <Input
                    type="text"
                    placeholder={filter.placeholder}
                    value={filterValues[filter.name] || ''}
                    onChange={(e) => handleFilterChange(filter.name, e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    fullWidth
                  />
                )}
                {filter.type === 'select' && (
                  <Select
                    placeholder={filter.placeholder}
                    value={filterValues[filter.name] || ''}
                    onChange={(e) => handleFilterChange(filter.name, e.target.value)}
                    options={filter.options || []}
                    fullWidth
                  />
                )}
              </div>
            ))}

            <Button
              variant="primary"
              onClick={handleSearch}
              className={styles.searchButton}
              leftIcon={<FaIcons.FaSearch />}
              loading={loading}
              disabled={loading}
            >
              {translate('global.search')}
            </Button>

            {showClearButton && (
              <Button
                variant="outline"
                onClick={handleClear}
                className={styles.clearButton}
                leftIcon={<FaIcons.FaTimes />}
              >
                {translate('global.clean')}
              </Button>
            )}
          </>
        )}
      </div>

      {/* Fila separada para chips de filtros activos */}
      {unifiedMode && activeChips.length > 0 && (
        <div className={styles.chipsRow}>
          <div className={styles.chipsContainer}>
            {activeChips.map(([field, filterValue]) => {
              // Usar la función de formateo si está disponible, sino mostrar el valor tal cual
              const displayValue = formatChipValue ? formatChipValue(field, filterValue) : filterValue;
              
              return (
                <div key={field} className={styles.chip}>
                  <span className={styles.chipLabel}>{getFieldLabel(field)}:</span>
                  <span className={styles.chipValue}>{displayValue}</span>
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => {
                      const newFilters = { ...activeFilters };
                      delete newFilters[field];
                      if (onFilter) {
                        onFilter(newFilters);
                      }
                    }}
                    disabled={loading}
                    aria-label={`Eliminar filtro ${getFieldLabel(field)}`}
                  >
                    <FaIcons.FaTimes size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

SearchFilter.propTypes = {
  onSearch: PropTypes.func,
  onFilter: PropTypes.func,
  onClear: PropTypes.func,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['text', 'select']).isRequired,
      name: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
      options: PropTypes.array, // For select type
    })
  ),
  activeFilters: PropTypes.object,
  placeholder: PropTypes.string,
  showClearButton: PropTypes.bool,
  className: PropTypes.string,
  loading: PropTypes.bool,
  unifiedMode: PropTypes.bool,
  selectedField: PropTypes.string,
  onFieldChange: PropTypes.func,
  fieldOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  statusFilter: PropTypes.bool,
  selectedStatus: PropTypes.string,
  onStatusChange: PropTypes.func,
  statusOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  clientTypeFilter: PropTypes.bool,
  selectedClientType: PropTypes.string,
  onClientTypeChange: PropTypes.func,
  clientTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
    })
  ),
  roleFilter: PropTypes.bool,
  selectedRole: PropTypes.string,
  onRoleChange: PropTypes.func,
  roleOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
    })
  ),
  formatChipValue: PropTypes.func,
  formatChipLabel: PropTypes.func,
  // Cuando es true, el contenedor maneja manualmente la lógica del filtro de status
  disableStatusAutoFilter: PropTypes.bool,
};

export default SearchFilter;
